import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { take } from 'rxjs/operators';

import { ApiError, MessageService } from '../../../../../core';

import { CampanhaSearchOrderBy } from '../../campanha-search-orderby';
import { CampanhaFilter } from '../../filter/campanha-filter';
import { CampanhaSearchHttpService } from '../../services';
import { campanhaStatusEmAndamento, isStatusEmAndamento } from '../../status';
import { AreaCampanhaSelect, createAreasCampanhasSelect, findAreaCampanha } from './area-campanha-select';
import { CampanhaSelect } from './campanha-select';
import { Subscription } from 'rxjs';

@Component({
    selector: 'sgt-campanha-select',
    templateUrl: './campanha-select.component.html',
    styleUrls: ['./campanha-select.component.less'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CampanhaSelectComponent), multi: true },
    ]
})
export class CampanhaSelectComponent implements OnInit {
    /** ID da campanha */
    @Input() ngModel: number;
    /** Ao alterar o ID da campanha*/
    @Output() ngModelChange = new EventEmitter<number>();

    /** Campanha */
    @Input() campanha: CampanhaSelect;
    @Output() campanhaChange = new EventEmitter<CampanhaSelect>();

    /** Campanhas */
    areasCampanhas: AreaCampanhaSelect[];

    /** Nome do input */
    @Input() name: string;
    /* ID do input */
    @Input() id: string;
    /* Se o valor é obrigatório */
    @Input() required: any;
    /* Se deve desabilitar o input */
    @Input() disabled: boolean;
    /** Se deve setar o focus automaticamente */
    @Input() autofocus = false;
    /** Se deve filtrar somente as campanhas em andamento */
    @Input() somenteEmAndamento = false;
    /** Campanhas sob demanda */
    @Input() sobDemanda: boolean = null;
    /** Objetivo de venda */
    @Input() objetivoVenda: boolean = null;
    /** Objetivo de bonificação */
    @Input() objetivoBonificacao: boolean = null;
    /** Objetivo de aquisição */
    @Input() objetivoAquisicao: boolean = null;
    /** Cliente vinculado à campanha */
    @Input() idClienteVinculado: number = null;
    /** Representante vinculado à campanha */
    @Input() idRepresentante: number = null;
    /** Se deve filtrar somente campanhas diretamente vinculadas ao cliente */
    @Input() somenteClientesVinculados: boolean = null;

    @ViewChild('select') select: ElementRef;
    currentSub$: Subscription;

    propagateChange: any = () => {};

    constructor(private messageService: MessageService,
                private campanhaSearchHttpService: CampanhaSearchHttpService) { }

    ngOnInit() {
        this.search();
    }

    ngOnChanges(changes: SimpleChanges) {
        for (const propName in changes) {
            if (!changes[propName].isFirstChange()) {
                // Pesquisando as campanhas ao alterar o filtro
                if ((propName === 'sobDemanda' ||
                    propName === 'objetivoVenda' ||
                    propName === 'objetivoBonificacao' ||
                    propName === 'objetivoAquisicao' ||
                    propName === 'idClienteVinculado' ||
                    propName === 'idRepresentante' ||
                    propName === 'somenteClientesVinculados')) {
                    this.search();
                }
            }
        }
    }

    /** Cria o filter para pesquisar as campanhas */
    private createFilter(): CampanhaFilter {
        const filter = new CampanhaFilter(null);
        // Somente em andamento
        if (this.somenteEmAndamento) {
            filter.statuses = [campanhaStatusEmAndamento.id];
        }
        // Sob demnada
        if (this.sobDemanda != null && this.sobDemanda !== undefined) {
            filter.sobDemanda = this.sobDemanda;
        }
        // Objetivo de venda
        if (this.objetivoVenda != null && this.objetivoVenda !== undefined) {
            filter.objetivoVenda = this.objetivoVenda;
        }
        // Objetivo de bonificação
        if (this.objetivoBonificacao != null && this.objetivoBonificacao !== undefined) {
            filter.objetivoBonificacao = this.objetivoBonificacao;
        }
        // Objetivo de aquisição
        if (this.objetivoAquisicao != null && this.objetivoAquisicao !== undefined) {
            filter.objetivoAquisicao = this.objetivoAquisicao;
        }

        // Cliente vinculado à campanha
        if (this.idClienteVinculado) {
            filter.idClienteVinculado = this.idClienteVinculado;
            filter.somenteClientesVinculados = this.somenteClientesVinculados;
        }

        // Representante vinculado à campanha
        if (this.idRepresentante) {
            filter.idsRepresentantes = [this.idRepresentante];
        }

        return filter;
    }

    /** Pesquisa as campanhas */
    private search() {
        const filter = this.createFilter();

        this.currentSub$?.unsubscribe();
        this.currentSub$ = this.campanhaSearchHttpService.searchSelect(filter, CampanhaSearchOrderBy.NomeArea, 'asc')
            .pipe(
                take(1)
            )
            .subscribe(
                campanhas => {
                    this.areasCampanhas = createAreasCampanhasSelect(campanhas);
                    // Quando receber a campanha pelo input
                    this.addCampanha(this.campanha);
                },
                (error: ApiError) => {
                    this.messageService.error(error.message, 'Erro ao pesquisar as campanhas', error);
                    this.areasCampanhas = [];
                }
            );
    }

    /** Adiciona a campanha informada ao select */
    addCampanha(campanha: CampanhaSelect) {
        if (!campanha) {
            return;
        }

        // Obtendo a área
        let area = findAreaCampanha(this.areasCampanhas, campanha.idArea);
        if (!area) {
            area = new AreaCampanhaSelect(campanha.idArea, campanha.nomeArea);
            this.areasCampanhas.push(area);
        }

        if (!area.hasCampanha(campanha.id)) {
            area.addCampanha(campanha);
        }
    }

    /** Verifica se a campanha está em andamento */
    isEmAndamento(status: number): boolean {
        return isStatusEmAndamento(status);
    }

    /** Ao alterar a campanha */
    onChange(idCampanha: number) {
        this.ngModel = idCampanha;
        this.propagateChange(this.ngModel);

        // Obtendo a campanha
        if (idCampanha) {
            for (let i = 0; i < this.areasCampanhas.length; i++) {
                const area = this.areasCampanhas[i];
                const campanha = area.findCampanha(idCampanha);
                if (campanha) {
                    this.campanha = campanha;
                    break;
                }
            }
        } else {
            this.campanha = null;
        }

        this.campanhaChange.emit(this.campanha);
    }

    /** Seta o focus no select */
    setFocus() {
        jQuery(this.select?.nativeElement).trigger('focus');
    }

    writeValue(value: number) {}

    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched() {}
}
